import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import axios from "axios";
//import { examSetupApi, setupError } from "../../Exams/examSetupApi";

const outlinedLabelProps = {
  shrink: true,
  sx: {
    backgroundColor: "#fff",
    color: "#344767",
    fontSize: "0.95rem",
    fontWeight: 500,
    px: 0.75,
    "&.Mui-focused": { color: "#1976d2" },
  },
};
function examSetupApi() {
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  if (!user?.client_id || !user?.college_id)
    throw new Error("Select a college before managing exam setup");
  const base = `${process.env.REACT_APP_API_BASE_URL}/api/exam-setup/${user.client_id}/${user.college_id}`;
  const token = sessionStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return {
    get: async (path, params) => (await axios.get(`${base}${path}`, { params, headers })).data.data,
    post: async (path, data) => (await axios.post(`${base}${path}`, data, { headers })).data.data,
    put: async (path, data) => (await axios.put(`${base}${path}`, data, { headers })).data.data,
    delete: async (path) => (await axios.delete(`${base}${path}`, { headers })).data,
  };
}

function setupError(error) {
  return error.response?.data?.message || error.message || "Unable to save exam setup";
}

export default function AcademicYears() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [academicYear, setAcademicYear] = useState("");
  const [active, setActive] = useState(true);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const api = examSetupApi();
  const load = async () => {
    try {
      setRows(await api.get("/academic-years"));
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    }
  };
  useEffect(() => {
    load();
  }, []);
  const reset = () => {
    setEditing(null);
    setAcademicYear("");
    setActive(true);
  };
  const rowsWithSNo = rows.map((row, index) => ({
    ...row,
    sno: index + 1,
  }));
  const save = async () => {
    setBusy(true);
    try {
      if (editing)
        await api.put(`/academic-years/${editing}`, {
          academic_year: academicYear,
          is_active: active,
        });
      else await api.post("/academic-years", { academic_year: academicYear, is_active: active });
      setMessage({ type: "success", text: "Academic year saved" });
      reset();
      await load();
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    } finally {
      setBusy(false);
    }
  };
  const edit = (row) => {
    setEditing(row.acyear_id);
    setAcademicYear(row.academic_year);
    setActive(row.is_active === 1);
  };
  const toggle = async (row) => {
    try {
      await api.put(`/academic-years/${row.acyear_id}`, {
        academic_year: row.academic_year,
        is_active: row.is_active !== 1,
      });
      await load();
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    }
  };
  const columns = [
    {
      field: "sno",
      headerName: "SNo",
      width: 80,
      sortable: false,
      filterable: false,
    },
    { field: "college_id", headerName: "College ID", minWidth: 120, flex: 0.7 },
    { field: "academic_year", headerName: "Academic Year", minWidth: 180, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.7,
      renderCell: (params) => {
        const isActive = params.row.is_active === 1;

        return (
          <Box
            sx={{
              px: 1.8,
              py: 0.5,
              borderRadius: "6px",
              fontWeight: 700,
              fontSize: "0.8rem",
              color: isActive ? "#2e7d32" : "#d32f2f",
              backgroundColor: isActive ? "#e8f5e9" : "#ffebee",
              border: `1px solid ${isActive ? "#a5d6a7" : "#ef9a9a"}`,
              minWidth: 75,
              textAlign: "center",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 240,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isActive = params.row.is_active === 1;

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="contained"
              onClick={() => edit(params.row)}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              Edit
            </Button>

            <Button
              size="small"
              variant="contained"
              onClick={() => toggle(params.row)}
              sx={{
                backgroundColor: isActive ? "#d32f2f" : "#2e7d32",
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: isActive ? "#b71c1c" : "#1b5e20",
                },
              }}
            >
              {isActive ? "Deactivate" : "Activate"}
            </Button>
          </Stack>
        );
      },
    },
  ];
  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, width: "100%", boxSizing: "border-box" }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontSize: { xs: "1.1rem", sm: "1.35rem" }, fontWeight: 700 }}
      >
        MANAGE ACADEMIC YEAR
      </Typography>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {/* Academic Year Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={2}>
            <TextField
              label="Academic Year"
              placeholder="2026-2027"
              InputLabelProps={outlinedLabelProps}
              value={academicYear}
              onChange={(event) => setAcademicYear(event.target.value)}
              size="medium"
              required
              fullWidth
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: { xs: 14, sm: 16 },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2} md={2}>
            <FormControlLabel
              control={
                <Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />
              }
              label="Active"
              sx={{ mx: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={5} md={3}>
            <Grid container spacing={1}>
              <Grid item xs={editing ? 8 : 12}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={save}
                  disabled={busy || !academicYear.trim()}
                  sx={{
                    minHeight: 42,
                    color: "#fff !important",
                    fontWeight: 700,
                    whiteSpace: "normal",
                    "&:hover": { color: "#fff !important" },
                    "&.Mui-disabled": {
                      color: "rgba(0, 0, 0, 0.26) !important",
                    },
                  }}
                >
                  {editing ? "Update Academic Year" : "Add Academic Year"}
                </Button>
              </Grid>

              {editing && (
                <Grid item xs={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={reset}
                    sx={{
                      minHeight: 42,
                      color: "#ffffff",
                      borderColor: "#d32f2f",
                      backgroundColor: "#b71c1c",
                      fontWeight: 700,
                      "&:hover": {
                        color: "#ffffff",
                        borderColor: "#b71c1c",
                        backgroundColor: "#b71c1c",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Academic Year DataGrid */}
      <Paper variant="outlined" sx={{ p: { xs: 0.5, sm: 2 }, minWidth: 0 }}>
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            height: { xs: 480, sm: 450 },
            overflowX: "auto",
            "& .MuiDataGrid-root": {
              fontSize: { xs: 12, sm: 14 },
            },
            "& .MuiDataGrid-columnHeaders": {
              fontWeight: 700,
            },
            "& .MuiDataGrid-footerContainer": {
              flexWrap: "wrap",
              minHeight: { xs: 80, sm: 52 },
            },
          }}
        >
          <DataGrid
            rows={rowsWithSNo}
            columns={columns}
            getRowId={(row) => row.acyear_id}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            pagination
            disableSelectionOnClick
            sx={{
              minWidth: { xs: 650, sm: "100%" },
              border: 0,
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
